import { inject, Injectable } from '@angular/core';
import {
  DefaultDisplayGuestDto,
  DisplayUser,
  DisplayUserEmail,
  FolderInvitation,
  FolderMember,
  GalleryInvitation,
  GalleryMemberDto,
} from '@nusign/picflow-backend-types';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { splitName } from '../lib/split-name';
import { unique } from '../lib/unique';
import { FolderMemberService } from './folder-member.service';
import { GalleryMemberService } from './gallery-member.service';
import { GalleryService } from './gallery.service';
import { GoogleContactsService } from './google-contacts.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { OrganizationService } from './organization.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { VisitorService } from './visitor.service';

export interface AutoCompleteItem {
  email: string;
  id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmailAutocompleteService {
  private state = inject(StateService);
  private galleryMemberService = inject(GalleryMemberService);
  private folderMemberService = inject(FolderMemberService);
  private organizationService = inject(OrganizationService);
  private googleContactsService = inject(GoogleContactsService);
  private visitorService = inject(VisitorService);
  private galleryService = inject(GalleryService);
  private storage = inject(StorageService);
  private loader = inject(ObservableDataLoaderService);

  public loadContactsFromGoogle() {
    this.googleContactsService.getConnections().then(connections => {
      const contacts = connections.map(c => {
        return <AutoCompleteItem>{
          id: `GOOGLE_CONTACT_${c.email}`,
          email: c.email,
          first_name: splitName(c.name).first_name,
          last_name: splitName(c.name).last_name,
        };
      });
      this.addContacts(contacts);
    });
  }

  private storedContacts$ = this.loader.get('stored-contacts', () => {
    const contacts = this.storage.get<AutoCompleteItem[]>('contacts') || [];
    return of(contacts);
  });

  public addContacts(contacts: AutoCompleteItem[]) {
    this.loader.update<AutoCompleteItem[]>('stored-contacts', storedContacts => {
      this.storage.set('contacts', [...storedContacts, ...contacts]);
      return unique([...storedContacts, ...contacts], 'email');
    });
  }

  private galleryMembers$ = combineLatest([this.state.activeTenant$, this.state.sharedWithMeActiveTenant$]).pipe(
    switchMap(([activeTenant]) => {
      if (!activeTenant) return of([] as AutoCompleteItem[]);
      return this.galleryMemberService
        .getAllGalleryMembers(true)
        .pipe(
          map(members =>
            members.map(member => this.mapUserToAutoCompleteItem(member.user as DisplayUserEmail)).filter(r => r?.email)
          )
        );
    })
  );

  private folderMembers$ = combineLatest([this.state.activeTenant$, this.state.sharedWithMeActiveTenant$]).pipe(
    switchMap(([activeTenant]) => {
      if (!activeTenant) return of([] as AutoCompleteItem[]);
      return this.folderMemberService
        .getAllFolderMembers(true)
        .pipe(
          map(members =>
            members.map(member => this.mapUserToAutoCompleteItem(member.user as DisplayUserEmail)).filter(r => r?.email)
          )
        );
    })
  );

  private tenantMembers$ = combineLatest([this.state.activeTenant$, this.state.sharedWithMeActiveTenant$]).pipe(
    switchMap(([activeTenant]) => {
      if (!activeTenant) return of([] as AutoCompleteItem[]);
      return this.organizationService
        .getMembers()
        .pipe(
          map(members =>
            members.map(member => this.mapUserToAutoCompleteItem(member as DisplayUserEmail)).filter(r => r?.email)
          )
        );
    })
  );

  private visitors$ = combineLatest([this.state.activeTenant$, this.state.sharedWithMeActiveTenant$]).pipe(
    switchMap(([activeTenant]) => {
      if (!activeTenant) return of([] as AutoCompleteItem[]);
      return this.visitorService.getAllVisitors().pipe(
        map(visitors => {
          return visitors
            .map(visitor => {
              const userOrGuest = visitor.guest || visitor.user;
              if (!userOrGuest?.email) return null;
              return <AutoCompleteItem>{
                email: userOrGuest.email.toLowerCase(),
                id: userOrGuest.id,
                first_name: this.getFirstName(userOrGuest),
                last_name: this.getLastName(userOrGuest),
                avatar_url: (userOrGuest as DisplayUser).avatar_url,
              };
            })
            .filter(r => r?.email);
        })
      );
    })
  );

  public results$: Observable<AutoCompleteItem[]> = combineLatest([
    this.galleryMembers$,
    this.folderMembers$,
    this.tenantMembers$,
    this.visitors$,
    this.storedContacts$,
  ]).pipe(
    map(([galleryMembers, folderMembers, tenantMembers, visitors, storedContacts]) =>
      unique([...galleryMembers, ...folderMembers, ...tenantMembers, ...visitors, ...storedContacts], 'email')
    )
  );

  public getResultsForGallery(galleryId: string) {
    return this.state.activeTenant$.pipe(
      switchMap(activeTenant => {
        if (activeTenant) {
          return combineLatest([
            this.tenantMembers$,
            this.galleryMemberService
              .getGalleryMembers(galleryId)
              .pipe(
                map(members => members.map(member => this.mapUserToAutoCompleteItem(member.user as DisplayUserEmail)))
              ),
            this.galleryService
              .getGalleryOwner(galleryId, true)
              .pipe(map(owner => this.mapUserToAutoCompleteItem(owner))),
          ]).pipe(
            map(([tenantMembers, galleryMembers, owner]) => {
              const members = unique([...tenantMembers, ...galleryMembers], 'id');
              return [members, owner] as const;
            })
          );
        }

        return combineLatest([
          this.galleryMemberService
            .getGalleryMembers(galleryId)
            .pipe(
              map(members => members.map(member => this.mapUserToAutoCompleteItem(member.user as DisplayUserEmail)))
            ),
          this.galleryService
            .getGalleryOwner(galleryId, false)
            .pipe(map(owner => this.mapUserToAutoCompleteItem(owner))),
        ]);
      }),
      map(([results, owner]) => {
        return unique([...results, owner], 'id');
      })
    );
  }

  public getErrorMessage(
    email: string,
    members: (GalleryMemberDto | FolderMember)[],
    invites: (GalleryInvitation | FolderInvitation)[],
    owner: DisplayUserEmail
  ): string {
    if (!members) return 'Loading...';
    if (!invites) return 'Loading...';
    if (!owner) return 'Loading...';
    if (members.some(m => (m.user as DisplayUserEmail).email?.toLowerCase() === email)) {
      return 'This person has already been invited.';
    }
    if (invites.some(m => m.email?.toLowerCase() === email)) {
      return 'This person has already been invited.';
    }
    if (owner.email?.toLowerCase() === email) {
      return 'This is the owner. They already have access.';
    }
  }

  public filterResults(
    emailString: string,
    allResults: AutoCompleteItem[],
    existingEmails: string[],
    members: (GalleryMemberDto | FolderMember)[],
    invites: (GalleryInvitation | FolderInvitation)[],
    owner: DisplayUserEmail
  ) {
    if (!allResults || !emailString || !existingEmails) return [] as AutoCompleteItem[];

    return allResults.filter(r => {
      if (!r.email.includes(emailString.toLowerCase())) return false;
      if (existingEmails.some(e => e === r.email.toLowerCase())) return false;
      const errorMessage = this.getErrorMessage(r.email, members, invites, owner);
      return !errorMessage;
    });
  }

  private getFirstName(userOrGuest: DefaultDisplayGuestDto | DisplayUser) {
    if ('first_name' in userOrGuest) return userOrGuest.first_name;
    const { first_name } = splitName(userOrGuest.name);
    return first_name;
  }

  private getLastName(userOrGuest: DefaultDisplayGuestDto | DisplayUser) {
    if ('last_name' in userOrGuest) return userOrGuest.last_name;
    const { last_name } = splitName(userOrGuest.name);
    return last_name;
  }

  private mapUserToAutoCompleteItem(user: DisplayUserEmail): AutoCompleteItem {
    return <AutoCompleteItem>{
      email: user.email?.toLowerCase() ?? '',
      id: user.id,
      first_name: this.getFirstName(user),
      last_name: this.getLastName(user),
      avatar_url: user.avatar_url,
    };
  }
}
